import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { Calendar, ChevronRight, Flame, Ruler } from "lucide-react";

/**
 * Unified history surface — every finished workout AND every logged measurement,
 * grouped by calendar date (newest first). Tabs let the user filter to a
 * specific stream. Workouts and measurements have distinct left-border colours
 * + icons so they're discernible while scrolling.
 */
const TABS = [
    { key: "all", label: "Everything" },
    { key: "workouts", label: "Workouts" },
    { key: "measurements", label: "Measurements" },
];

const MEAS_FIELDS = [
    { key: "body_weight", label: "Weight" },
    { key: "body_fat_pct", label: "Body Fat %" },
    { key: "waist", label: "Waist" },
    { key: "chest", label: "Chest" },
    { key: "arm", label: "Arm" },
    { key: "thigh", label: "Thigh" },
];

export default function PreviousWorkouts() {
    const [logs, setLogs] = useState(null);
    const [measurements, setMeasurements] = useState(null);
    const [params, setParams] = useSearchParams();
    const tab = TABS.find(t => t.key === params.get("tab"))?.key || "all";

    useEffect(() => {
        api.get("/workouts?limit=500")
            .then((r) => setLogs((r.data || []).filter((l) => l.finished_at)))
            .catch(() => setLogs([]));
        api.get("/progress/measurements")
            .then((r) => setMeasurements(r.data || []))
            .catch(() => setMeasurements([]));
    }, []);

    const items = useMemo(() => {
        if (logs === null || measurements === null) return null;
        const list = [];
        if (tab === "all" || tab === "workouts") {
            for (const l of logs) {
                list.push({
                    type: "workout",
                    when: l.finished_at,
                    payload: l,
                });
            }
        }
        if (tab === "all" || tab === "measurements") {
            for (const m of measurements) {
                list.push({
                    type: "measurement",
                    when: m.date,
                    payload: m,
                });
            }
        }
        list.sort((a, b) => new Date(b.when) - new Date(a.when));
        // Group by yyyy-mm-dd
        const grouped = {};
        for (const i of list) {
            const k = new Date(i.when).toISOString().slice(0, 10);
            (grouped[k] ||= []).push(i);
        }
        return grouped;
    }, [logs, measurements, tab]);

    if (items === null) {
        return <div className="tactical-card p-6 text-sm text-muted-foreground">Loading history…</div>;
    }

    const dates = Object.keys(items).sort().reverse();
    const totalCount = Object.values(items).reduce((acc, arr) => acc + arr.length, 0);

    return (
        <div className="space-y-6" data-testid="previous-workouts-page">
            <div>
                <div className="label-eyebrow text-accent-red">History</div>
                <h1 className="font-display text-4xl font-black uppercase sm:text-5xl">Your Record</h1>
                <p className="mt-2 max-w-2xl text-sm text-muted-foreground">
                    Every finished session and every body measurement, dated and archived. Workouts are flagged in red; measurements are in cool blue. Tap any workout to open it read-only.
                </p>
            </div>

            <div className="flex flex-wrap gap-2" data-testid="history-tabs">
                {TABS.map(t => (
                    <button
                        key={t.key}
                        onClick={() => setParams({ tab: t.key })}
                        data-testid={`history-tab-${t.key}`}
                        className={`border px-4 py-2 text-xs font-bold uppercase tracking-wider transition ${tab === t.key ? "border-accent-red bg-accent-red text-white" : "border-border bg-card hover:bg-secondary"}`}
                    >
                        {t.label}
                    </button>
                ))}
            </div>

            {totalCount === 0 ? (
                <div className="tactical-card p-8 text-center text-sm text-muted-foreground" data-testid="previous-workouts-empty">
                    Nothing yet. Go log a session or a measurement — your record starts now.
                </div>
            ) : (
                <div className="space-y-6">
                    {dates.map((date) => (
                        <section key={date} data-testid={`prev-date-${date}`}>
                            <div className="flex items-center gap-2 border-b border-border pb-2">
                                <Calendar className="h-4 w-4 text-accent-red" />
                                <h2 className="font-display text-lg font-black uppercase">
                                    {new Date(date).toLocaleDateString(undefined, { weekday: "short", year: "numeric", month: "short", day: "numeric" })}
                                </h2>
                            </div>
                            <div className="mt-3 space-y-2">
                                {items[date].map((row) =>
                                    row.type === "workout"
                                        ? <WorkoutRow key={`w-${row.payload.log_id}`} log={row.payload} />
                                        : <MeasurementRow key={`m-${row.payload.id}`} m={row.payload} />
                                )}
                            </div>
                        </section>
                    ))}
                </div>
            )}
        </div>
    );
}

function WorkoutRow({ log }) {
    return (
        <Link
            to={`/session/${log.log_id}`}
            data-testid={`prev-log-${log.log_id}`}
            className="tactical-card flex items-center justify-between gap-3 border-l-4 border-l-accent-red p-4 transition hover:border-accent-red/60"
        >
            <div className="min-w-0">
                <div className="label-eyebrow text-accent-red">
                    {log.program_key === "full_body" ? "Workout · Full Body" : "Workout · Upper / Lower"}
                </div>
                <div className="mt-1 font-display text-lg font-black uppercase">{log.day_name || "Workout"}</div>
                <div className="mt-0.5 text-xs text-muted-foreground">
                    {(log.exercises?.length || 0)} exercises ·{" "}
                    {(log.exercises || []).reduce((acc, ex) => acc + (ex.sets?.length || 0), 0)} working sets
                </div>
            </div>
            <div className="flex items-center gap-3">
                <div className="hidden text-right sm:block">
                    <div className="flex items-center justify-end gap-1 text-xs text-accent-red">
                        <Flame className="h-3 w-3" />
                        <span className="font-display font-black tabular-nums">+{log.exp_earned || 0}</span>
                    </div>
                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground">EXP earned</div>
                </div>
                <ChevronRight className="h-4 w-4 text-muted-foreground" />
            </div>
        </Link>
    );
}

function MeasurementRow({ m }) {
    const filled = MEAS_FIELDS.filter(f => m[f.key] != null && m[f.key] !== "");
    return (
        <div
            data-testid={`prev-meas-${m.id}`}
            className="tactical-card flex items-start justify-between gap-3 border-l-4 border-l-sky-500 bg-sky-500/5 p-4"
        >
            <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                    <Ruler className="h-3.5 w-3.5 text-sky-400" />
                    <div className="label-eyebrow text-sky-400">Measurement</div>
                </div>
                <div className="mt-1 font-display text-lg font-black uppercase">
                    {filled.length} value{filled.length === 1 ? "" : "s"} logged
                </div>
                <div className="mt-1.5 flex flex-wrap gap-x-4 gap-y-1 text-xs text-foreground/85">
                    {filled.length === 0 && <span className="text-muted-foreground">No values</span>}
                    {filled.map(f => (
                        <span key={f.key} className="tabular-nums" data-testid={`prev-meas-${m.id}-${f.key}`}>
                            <span className="text-muted-foreground">{f.label}:</span>{" "}
                            <span className="font-bold">{m[f.key]}</span>
                        </span>
                    ))}
                </div>
            </div>
        </div>
    );
}
